import { Minimize2 } from "lucide-react";
import { CSSProperties } from "react";
import { Link, useSearchParams } from "react-router";
import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";

import {
  formatDurationMs,
  useQaAggregates,
  type AggregateWindow,
} from "../../../../qa-automation-api";
import { SkeletonBlock } from "../../../../skeleton-block";
import { ScrollShadow } from "../../../../scroll-shadow";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import styles from "./slowest-tests.module.css";

const WINDOWS = new Set<AggregateWindow>(["today", "7d", "30d", "6m", "all"]);

export function QaSlowestTestsPage() {
  useDocumentTitle("Slowest tests");
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("window") as AggregateWindow | null;
  const window = requested && WINDOWS.has(requested) ? requested : "30d";
  const query = useQaAggregates(window);
  const rows = query.data?.perTestAvgDurationMs ?? [];
  const maxDuration = rows[0]?.avgMs ?? 1;
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();

  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName: "qa-card-slowest-tests" } as CSSProperties}
        title="Slowest tests"
        count={query.isLoading ? undefined : rows.length}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to="/qa-automation" viewTransition>
              <Minimize2 aria-hidden="true" />
            </Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <ol className={styles.rankedList}>
              {query.isLoading
                ? Array.from({ length: 8 }, (_, index) => (
                    <li key={index} className={styles.rankedRow} aria-hidden="true">
                      <span className={styles.rankedIndex}>{index + 1}</span>
                      <div className={styles.rankedMain}><SkeletonBlock height={13} width="70%" /></div>
                    </li>
                  ))
                : rows.map((row, index) => (
                    <li key={`${row.title}-${index}`} className={styles.rankedRow} title={row.title}>
                      <span className={styles.rankedIndex}>{index + 1}</span>
                      <div className={styles.rankedMain}>
                        <div className={styles.rankedPrimary}>{row.primary || row.title}</div>
                        {row.context && <div className={styles.rankedContext}>{row.context}</div>}
                        <div className={styles.rankedBarTrack}>
                          <div className={`${styles.rankedBarFill} ${styles.rankedBarDuration}`} style={{ width: `${Math.round((row.avgMs / maxDuration) * 100)}%` }} />
                        </div>
                      </div>
                      <div className={styles.rankedValues}>
                        <div className={styles.rankedPrimaryValue}>{formatDurationMs(row.avgMs)}</div>
                        <div className={styles.rankedSecondaryValue}>{row.runs} run{row.runs === 1 ? "" : "s"}</div>
                      </div>
                    </li>
                  ))}
            </ol>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} />
        </div>
      </PageSection>
    </div>
  );
}
